import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelTokenStatic } from "axios";
import * as Sentry from "@sentry/react";
import { ScreenType } from "components/WarningSnackbar";
import useAuthToken from "hooks/useAuthToken";
import i18n from "i18n";
import { FALLBACK_LOCALE } from "i18n/config";
import { EventDispatchLogout, EventDispatchSetPassword, EventDispatchSnackbar, EventDispatchTeamSuspended } from "router/events";
import { isTeamSuspendedError, isTeamSuspendedLogin } from "utils/teamSuspended";
import { PATHS } from "router/config/paths";
import { trackEvent } from "utils/analytics";
import { EVENTS } from "utils/analytics.events";
import { getSearchParams } from "utils";
import { reportError, inferFeatureArea, normalizePath } from "utils/sentry";
import { SITE_IMAGES_PREVIEW_PATH } from "api/statics/sitePreviewPath";
import { httpMethodOf, paywallCreditsOf, paywallMessageTypeOf, paywallPopupReasonOf, paywallPopupTypeOf, requestIdOf, requestPathOf } from "utils/sentry/errorCategory";
import { httpStatusOf } from "utils/httpStatusOf";

declare global {
  interface Window {
    __designCanvasSessionId?: string;
  }
}

declare module "axios" {
  interface AxiosRequestConfig {
    /**
     * Opt this ONE request out of the response interceptor: no logout, no snackbar / paywall, no
     * `_handled` resolve, no Sentry report — every error simply rejects with the raw axios error and
     * the caller owns all of it. For background calls the user never asked for (the session renewal in
     * `hooks/useSessionRefresh`), where a 402 paywall or a 503 "servers busy" popping up out of nowhere
     * is the bug. Never set it on a user-initiated request.
     */
    skipGlobalErrorHandling?: boolean;
    /**
     * K99: the request reads a v3 route that answers with its own error body (`{ error: { code,
     * message, … } }`). Its 403 / 409 / 503 reach the caller as a rejected error — no logout, no
     * snackbar — so the screen shows the API's words. 401 still logs out and 402 still shows the
     * global paywall. Unflagged requests are handled exactly as before. With both flags set,
     * `skipGlobalErrorHandling` wins.
     */
    v3Errors?: boolean;
  }
}

interface Interceptor {
  onFulfilled: (config: AxiosRequestConfig) => AxiosRequestConfig;
  onRejected: (error: AxiosError) => Promise<AxiosError>;
}

export interface HttpClientOptions extends AxiosRequestConfig {
  requestInterceptors?: Interceptor[];
  responseInterceptors?: Interceptor[];
}

export interface ApiResponse<T> extends AxiosResponse {
  page?: any;
  result?: any;
  itemsCount?: number;
  pageCount?: number;
  pageSize?: number;
  pageNumber?: number;
  responseData: T;
  hasError: boolean;
  message: any;
  _handled?: boolean;
  responseCode?: number;
}

/**
 * The one endpoint whose 503 is retryable rather than "our servers are busy" (REF-6354):
 * a just-uploaded ZIP still inside the ClamAV scan window. Matched exactly — see the call
 * site for why a substring test is not good enough.
 */
export const MANNEQUIN_STUDIO_START_PATH = "mannequin-studio/start";

export function isMannequinStudioStartRequest(config?: { url?: string; method?: string }): boolean {
  if (!config?.url) return false;
  if ((config.method || "").toLowerCase() !== "post") return false;

  // Drop any query/hash, then compare the bare path. Leading slashes are tolerated because
  // callers pass the path relative to the axios baseURL and are inconsistent about it.
  const path = config.url.split("?")[0].split("#")[0].replace(/^\/+/, "").replace(/\/+$/, "");

  return path === MANNEQUIN_STUDIO_START_PATH;
}

/**
 * Tech pack re-lay (pattern_block_stitch): its 409s (`techpack_relay_job_running`, `_in_progress`,
 * `_superseded`, `_no_agent_result`, `_no_pattern_block`) and 503s (`techpack_relay_failed`,
 * `techpack.project_validation_unavailable`, `techpack_agent_not_configured`) each need a DIFFERENT
 * recovery, and the generic branches below resolve `_handled` with `responseData: null` — the code
 * is gone before any caller sees it. Nothing is written on any of them, so the single call site
 * (`TechPackProvider:reLayMarker`) owns the message. Exact path + POST, same reasoning as above.
 */
export const TECHPACK_RELAY_PATH = "techpack/pages/re-lay";

export function isTechpackRelayRequest(config?: { url?: string; method?: string }): boolean {
  if (!config?.url) return false;
  if ((config.method || "").toLowerCase() !== "post") return false;

  const path = config.url.split("?")[0].split("#")[0].replace(/^\/+/, "").replace(/\/+$/, "");

  return path === TECHPACK_RELAY_PATH;
}

/**
 * Tech pack start (2026-09-30): `409 techpack_drawing_in_progress` means a drawing the start would draw is
 * already being drawn (`detail.view` names it) — the user waits for that card, nothing was reserved, and
 * the generic "conflict" snackbar would hide which view. ONLY that code rejects to the caller
 * (`TechPackProvider:createTechpackInfo`); every other 409 on this route keeps the generic branch.
 */
export const TECHPACK_EXTRACT_ALL_PATH = "techpack/pages/extract-all";

export function isTechpackDrawingInProgress(error?: { config?: { url?: string; method?: string }; response?: { data?: any } }): boolean {
  const config = error?.config;
  if (!config?.url) return false;
  if ((config.method || "").toLowerCase() !== "post") return false;

  const path = config.url.split("?")[0].split("#")[0].replace(/^\/+/, "").replace(/\/+$/, "");
  if (path !== TECHPACK_EXTRACT_ALL_PATH) return false;

  const data = error?.response?.data;

  return (data?.detail?.error_code ?? data?.responseData?.error_code) === "techpack_drawing_in_progress";
}

/**
 * REF-6975: the Account → Usage "Spend detail" list and its CSV. Both render their OWN inline error
 * with a Retry, so a 503 must reject to that caller instead of also raising the blocking "servers
 * busy" modal. Read-only GETs, so a retry can never double-charge. EXACT paths + GET (same reasoning
 * as the mannequin matcher above): the summary `teams/usage-report` keeps the generic 503 handling.
 */
export const SPEND_DETAIL_PATHS = ["teams/usage-report/items", "teams/usage-report/items.csv"];

export function isSpendDetailRequest(config?: { url?: string; method?: string }): boolean {
  if (!config?.url) return false;
  if ((config.method || "get").toLowerCase() !== "get") return false;

  const path = config.url.split("?")[0].split("#")[0].replace(/^\/+/, "").replace(/\/+$/, "");

  return SPEND_DETAIL_PATHS.includes(path);
}

export default class HttpClient {
  private readonly axios: AxiosInstance;
  private authToken: string | null;

  CancelToken: CancelTokenStatic;
  componentDidMount() {
    // Equivalent to useEffect(() => { ... }, [])
    this.authToken = useAuthToken.getAuthTokenFromLocalStorage();
  }

  componentWillUnmount() {
    // Clean up any resources or subscriptions
    // Equivalent to useEffect(() => { return () => { ... } }, [])
  }

  constructor(baseURL: string, options: HttpClientOptions = {}) {
    const { requestInterceptors = [], responseInterceptors = [], headers = {}, ...rest } = options;

    this.authToken = useAuthToken.getAuthTokenFromLocalStorage();

    if (this.authToken) {
      headers.Authorization = `Bearer ${this.authToken}`;
    }

    type SearchParams = { [key: string]: string | number | boolean };
    const searchParams: SearchParams = getSearchParams();

    // Convert the entire searchParams object to a JSON string and assign it to headers
    headers["refabric_param"] = JSON.stringify(searchParams);

    this.CancelToken = axios.CancelToken;

    this.axios = axios.create({
      baseURL,
      headers,
      ...rest,
    });

    this.axios.interceptors.request.use((config) => {
      const language = i18n.language || FALLBACK_LOCALE;
      if (config.headers) {
        config.headers["cache-control"] = "no-cache";
        config.headers["Accept-Language"] = language;
      } else {
        config.headers = { "cache-control": "no-cache", "Accept-Language": language };
      }

      return config;
    });

    this.axios.interceptors.response.use(
      (conf) => conf,
      (error) => {
        if (error.config?.skipGlobalErrorHandling) return Promise.reject(error);

        const requestPath = error.config?.url;
        let panel = "";
        const currentRoute = `${window.location.pathname}${window.location.search}`;
        const isOnDesignCanvas = window.location.pathname === PATHS.DESIGN_STUDIO;

        const editTexts = ["brush", "magic", "lasso", "cleanup"];
        // REF-6708: telemetry takes only reviewed facts off the error (utils/sentry/errorCategory) — never
        // `error.config` / `error.response` values raw. Control flow below still reads the raw values.
        const logCanvasApiError = (statusCode: number | undefined, extra?: Record<string, unknown>) => {
          if (!isOnDesignCanvas) return;

          const payload = {
            session_id: window.__designCanvasSessionId || "unknown",
            route: currentRoute,
            request_path: requestPathOf(error) || "unknown",
            status_code: statusCode,
            ...extra,
          };

          trackEvent(EVENTS.DESIGN_CANVAS.API_ERROR, payload);
          Sentry.addBreadcrumb({
            category: "design-canvas",
            message: "canvas api error",
            level: (statusCode ?? 0) >= 500 ? "error" : "warning",
            data: payload,
          });
        };

        function getScreenType() {
          // REF-7280: the photoshoot flows' own requests are named by their PATH, ahead of the body/query heuristics below —
          // the free photo analyses (which still need the photoshoot permission) and Ghost's create matched nothing and
          // fell to "", whose copy is the Training paywall; a body that merely contains "photoshooting_mannequin" (a
          // project name) or a query carrying an edit word must not re-route them either.
          const pathOnly = (requestPath || "").split("?")[0].replace(/\/+$/, "");
          if (/(^|\/)photoshoot\/looks\/process$/.test(pathOnly)) return ScreenType.Tryon;
          if (/(^|\/)photoshoot\/mannequin\/analyze$/.test(pathOnly)) return ScreenType.Mannequin;
          if (/(^|\/)ghost_photoshoot\/(analyze|projects)$/.test(pathOnly)) return ScreenType.Ghost;

          if (editTexts.some((text) => requestPath.includes(text))) return ScreenType.Edit;
          else if (error.config?.data?.includes("photoshooting_mannequin")) {
            panel = "banner";

            return ScreenType.Mannequin;
          } else if (requestPath.includes("mannequin-studio")) return ScreenType.Mannequin;
          else if (requestPath.includes("studio")) return ScreenType.Marketplace;
          else if (requestPath.includes("photoshoot-edit")) return ScreenType.Marketplace;
          else if (requestPath.includes("start_photoshooting")) return ScreenType.Marketplace;
          else if (requestPath.includes("train")) return ScreenType.Train;
          else if (requestPath.includes("team")) return ScreenType.Team;
          else if (requestPath.includes("tryon")) return ScreenType.Tryon;
          else if (error.config?.params?.filter === "tryon") return ScreenType.Tryon;
          else if (requestPath.includes("design")) return ScreenType.Design;
          else if (error.config?.params?.filter === "photoshooting_mannequin") return ScreenType.Tryon;
          else if (error.config?.params?.filter === "ghost_shooting") return ScreenType.Tryon;
          else if (error.config?.params?.filter === "vton") return ScreenType.Tryon;
          else return "";
        }

        // New /v1/teams admin-management endpoints use 403 ("Admin required" / "Email domain mismatch")
        // and 409 (seat limit, already-member, invite-not-pending) as primary error channels with
        // semantic payloads. Exempt them from the generic logout / 409-snackbar interceptor branches
        // so callers can render inline UX. 401 still flows through (real session expiry).
        const isTeamsManagementUrl =
          !!requestPath &&
          (requestPath.includes("teams/roster") ||
            requestPath.includes("teams/invite") ||
            requestPath.includes("teams/members/") ||
            requestPath.includes("teams/credit-usage") ||
            requestPath.includes("teams/credit-policy") ||
            requestPath.includes("teams/usage-report") ||
            // P4-F4: `POST teams/accept_member_invite` (token in the body) and the deprecated `…/{token}` GET alike.
            requestPath.includes("teams/accept_member_invite"));

        const isMiroUrl = !!requestPath && requestPath.includes("integrations/miro");

        // REF-5421 set-password endpoint: it authenticates a guest who STILL carries the
        // must_set_password gate (via the gate-exempt BE dependency), so its own 400 (weak /
        // mismatch) / 409 (account already has a password) / 401 must reach the page's catch
        // for inline UX — never the generic logout / 409-snackbar branches below.
        if (requestPath?.includes("users/set-password")) return Promise.reject(error);

        // STRIPE P5-F2 (PD7, v3 P5-B11): a suspended team keeps its session. `403 {"error":{"code":"team_suspended"}}` from
        // any gated route — teams-admin and the five billing routes included (it arrives BEFORE their action-item envelope)
        // — raises the suspended-team screen: no logout, no snackbar, no paywall. Ahead of must_set_password (v3 precedence).
        // Keyed on the code only; any other 403 falls through to the branches below exactly as before.
        if (isTeamSuspendedError(error)) {
          window.dispatchEvent(new CustomEvent(EventDispatchTeamSuspended));

          return Promise.resolve({
            data: { data: [], responseData: null, responseCode: 403, _handled: true },
          });
        }

        // REF-5421 hard gate: a passwordless guest must set a password. The BE returns
        // 403 { responseData: { error_code: "must_set_password" } } from ANY gated endpoint.
        // Route to the set-password screen instead of clearing the token / logging out.
        if (error.response?.status === 403 && error.response?.data?.responseData?.error_code === "must_set_password") {
          window.dispatchEvent(new CustomEvent(EventDispatchSetPassword));

          return Promise.reject(error);
        }

        // K99: a flagged v3 request owns its 403 / 409 / 503. 401 (session expiry) and 402 (the paywall,
        // already shown globally) still go through the branches below.
        if (error.config?.v3Errors && [403, 409, 503].includes(error.response?.status)) {
          return Promise.reject(error);
        }

        if (error.response?.status === 403 || error.response?.status === 401) {
          //TOKEN EXPIRED

          // do not restart the page so user can see the error message. magic-login is an auth URL
          // too: a 401 there means a bad/expired token (NOT session expiry), so reject and let the
          // page surface its "request a new link" UX instead of firing a global logout.
          // REF-7108: `users/google_login` too — a 401 there is an invalid Google credential and a 403 is a
          // banned account (`account_unavailable`), neither a session expiry. Resolving them as `_handled`
          // fired a global logout and handed the page a tokenless "success".
          if (
            error.config?.url?.includes("users/otp") ||
            error.config?.url?.includes("users/login") ||
            // Exact path, not `includes`: a lookalike URL must keep the normal session-expiry handling.
            (error.config?.url || "").split("?")[0].replace(/^\/+/, "") === "users/google_login" ||
            error.config?.url?.includes("users/magic-login") ||
            error.config?.url?.includes("sso/") ||
            // The backoffice preview link's own token, not the session: an expired link must leave the
            // previewer signed in. Exact path, for the same reason as google_login above.
            (error.config?.url || "").split("?")[0].replace(/^\/+/, "") === SITE_IMAGES_PREVIEW_PATH
          )
            return Promise.reject(error);

          // A teams 403 is a permission answer for the page — EXCEPT the legacy BE's "no credential at
          // all" 403: FastAPI's APIKeyHeader answers a request without an Authorization header with
          // exactly {"detail":"Not authenticated"} before any route code runs (REF-6708). That is a
          // missing session, so it logs out like a 401. Any other teams 403 still rejects.
          const isUnauthenticated403 = error.response?.status === 403 && error.response?.data?.detail === "Not authenticated";
          if (error.response?.status === 403 && isTeamsManagementUrl && !isUnauthenticated403) return Promise.reject(error);

          logCanvasApiError(httpStatusOf(error), { reason: "auth_redirect" });

          useAuthToken.clearAuthTokenFromLocalStorage();
          window.dispatchEvent(new CustomEvent(EventDispatchLogout));

          return Promise.resolve({
            data: { data: [], responseData: null, responseCode: error.response?.status, _handled: true },
          });
        } else if (isTeamSuspendedLogin(error)) {
          // STRIPE P5-F2: the login family's 402 limited answer for a suspended team is not a paywall — the page keeps the
          // session and shows the suspended-team screen (pages/Login/hooks/useSuspendedLogin).
          return Promise.reject(error);
        } else if (error.response?.status === 402) {
          // Parse the new 402 error format from backend
          // responseData = { message_type: "popup" | "message", message: "", popup_type: "essentials" | ..., error_code?: "..." }
          const responseData = error.response?.data?.responseData;
          const messageType = responseData?.message_type || "";
          const popupType = responseData?.popup_type || "";
          const popupReason = responseData?.popup_reason || "";
          const screenType = getScreenType();
          // BE error-code adapter (Phase 2a). When BE sends `error_code` (under responseData per
          // REF-4919 BE contract) AND the catalog has a matching `errors:<code>` key, the snackbar
          // renders it. Otherwise Phase 5-6 falls back to `snackbar:402.default` so users always see
          // a localized message (and we never surface the raw catalog key as text).
          const errorCode = responseData?.error_code as string | undefined;
          // REF-5398: credit-gate 402s carry the shortfall numbers. On dual-bucket gates
          // (/start_tryon, /start_photoshooting) these reflect the MAIN pool only — never
          // derive "you need X more" copy from required - remaining.
          const requiredCredits = typeof responseData?.required_credits === "number" ? responseData.required_credits : null;
          const remainingCredits = typeof responseData?.remaining_credits === "number" ? responseData.remaining_credits : null;

          logCanvasApiError(402, {
            reason: "entitlement_redirect",
            screen: screenType,
            panel,
            message_type: paywallMessageTypeOf(error) ?? "",
            popup_type: paywallPopupTypeOf(error) ?? "",
            popup_reason: paywallPopupReasonOf(error) ?? "",
          });

          Sentry.addBreadcrumb({
            category: "http.paywall",
            message: "402 paywall response",
            level: "info",
            data: {
              request_path: requestPathOf(error) || "unknown",
              method: httpMethodOf(error),
              route: currentRoute,
              screen: screenType,
              panel,
              message_type: paywallMessageTypeOf(error) ?? "",
              popup_type: paywallPopupTypeOf(error) ?? "",
              popup_reason: paywallPopupReasonOf(error) ?? "",
              required_credits: paywallCreditsOf(error, "required_credits"),
              remaining_credits: paywallCreditsOf(error, "remaining_credits"),
            },
          });

          window.dispatchEvent(
            new CustomEvent(EventDispatchSnackbar, {
              detail: {
                type: 402,
                screen: screenType,
                panel: panel,
                messageType: messageType,
                popupType: popupType,
                messageKey: errorCode && i18n.exists(`errors:${errorCode}`) ? `errors:${errorCode}` : "snackbar:402.default",
                popupReason: popupReason,
                requiredCredits: requiredCredits,
                remainingCredits: remainingCredits,
              },
            })
          );

          // Reject so .catch() handlers run and can reset loading states
          // UI feedback is already handled by the snackbar/modal dispatch above
          return Promise.reject(error);
        } else if (error.response?.status === 409) {
          // Miro's 409s are coded states (`miro.not_connected` / `reauth_required` / `not_configured`),
          // not "a design is being generated". The generic branch drops the body and shows that copy,
          // so the prefix rejects and the Miro callers render the right state (REF-6702).
          if (isTeamsManagementUrl || isMiroUrl || isTechpackRelayRequest(error.config) || isTechpackDrawingInProgress(error)) return Promise.reject(error);

          // BE error-code adapter, same shape the 402 and 503 branches already use. Kadir's
          // refactor made `toggle-share` answer 409 `share_mode_conflict` when the resource is
          // shared with SELECTED PEOPLE — the toggle must not silently drop the recipient list.
          // Without this mapping the user got the generic `snackbar:409.default` ("conflict"),
          // which says nothing about why the switch refused. `errors:<code>` only when the key
          // exists, so an unknown code can never surface the raw catalog key as text.
          const errorCode = error.response?.data?.responseData?.error_code as string | undefined;
          window.dispatchEvent(
            new CustomEvent(EventDispatchSnackbar, {
              detail: {
                type: 409,
                messageKey: errorCode && i18n.exists(`errors:${errorCode}`) ? `errors:${errorCode}` : "snackbar:409.default",
              },
            })
          );

          // Resolved shape is UNCHANGED on purpose: `RefabricProURL` is the app-wide singleton and
          // `_handled: true` is the repo's 409 contract ([HTTP_HANDLED_RESPONSE]). Rejecting here —
          // even only for `share_mode_conflict` — would reach every 409 caller in the app, most of
          // which have no rejection path. Callers detect the failure via `_handled`.
          return Promise.resolve({
            data: { data: [], responseData: null, responseCode: 409, _handled: true },
          });
        } else if (error.response?.status === 503) {
          // REF-6354: `mannequin-studio/start` answers 503 for a RETRYABLE condition — the
          // uploaded ZIP is still inside the ClamAV scan window (22-34s), not "our servers are
          // busy". The generic branch below both mis-describes it AND resolves `_handled`,
          // which discards the axios error, the `Retry-After` header and the BE's `detail`, so
          // no caller can retry or explain. Reject instead and let the single call site own it
          // (PhotoshootStudio.handleStartPhotoshoot).
          //
          // Scoped by path on purpose — the same shape this file already uses for
          // `users/set-password` and the teams endpoints. NOT a global change:
          // `RefabricProURL` is the app-wide singleton, so a blanket 503 passthrough (or
          // retry) would reach hundreds of non-idempotent POSTs — purchases, generation jobs,
          // exports — where a second attempt is a second charge.
          // EXACT path + POST, never `includes`: a substring match would also exempt
          // `mannequin-studio/start-preview`, a future `.../start/anything`, and any URL whose
          // QUERY happens to carry that text — each of which would silently lose the global 503
          // modal and start rejecting at a call site with no handler.
          if (isMannequinStudioStartRequest(error.config) || isTechpackRelayRequest(error.config)) return Promise.reject(error);
          // REF-6975: Spend detail owns its 503 inline (see isSpendDetailRequest).
          if (isSpendDetailRequest(error.config)) return Promise.reject(error);

          const errorCode = error.response?.data?.responseData?.error_code as string | undefined;
          window.dispatchEvent(
            new CustomEvent(EventDispatchSnackbar, {
              detail: {
                type: 503,
                messageKey: errorCode && i18n.exists(`errors:${errorCode}`) ? `errors:${errorCode}` : "snackbar:503.default",
              },
            })
          );

          return Promise.resolve({
            data: { data: [], responseData: null, responseCode: 503, _handled: true },
          });
        }

        const status = error.response?.status;
        const shouldReport = !axios.isCancel(error) && status !== undefined && status >= 500 && status !== 503;

        if (shouldReport) {
          const featureArea = inferFeatureArea(requestPathOf(error));
          logCanvasApiError(httpStatusOf(error), { reason: "server_error" });
          reportError(error, {
            source: "http",
            featureArea,
            level: "error",
            httpStatus: httpStatusOf(error),
            requestPath: requestPathOf(error),
            requestId: requestIdOf(error),
            route: currentRoute,
            method: httpMethodOf(error),
            extras: {
              session_id: window.__designCanvasSessionId,
            },
            // Sanitise FIRST (REF-6708): normalizePath rewrites a UUID even inside a segment, which would
            // turn a token into `:id.<rest>`. `[redacted]` passes through normalizePath unchanged.
            // `requestPathOf` IS that sanitising pass (`sanitizeRoute`).
            fingerprint: ["http", featureArea, String(httpStatusOf(error)), normalizePath(requestPathOf(error))],
          });
        }

        return Promise.reject(error);
      }
    );

    requestInterceptors.forEach((interceptorConfig) => {
      this.axios.interceptors.request.use(
        (conf) => interceptorConfig.onFulfilled(conf),
        (error) => interceptorConfig.onRejected(error)
      );
    });

    responseInterceptors.forEach((interceptorConfig) => {
      this.axios.interceptors.response.use(
        (conf) => interceptorConfig.onFulfilled(conf),
        (error) => interceptorConfig.onRejected(error)
      );
    });
  }

  async request(options: AxiosRequestConfig): Promise<AxiosResponse> {
    const response = await this.axios.request(options);

    return response.data;
  }

  async get<T>(path: string, options: AxiosRequestConfig = {}): Promise<ApiResponse<T>> {
    const headers = {
      ...this.axios.defaults.headers.common,
      ...options.headers,
      ...this.authHeader(),
    };
    const response = await this.axios.get(path, { ...options, headers });

    return response.data;
  }

  async post<T, D = unknown>(path: string, data: D, options?: AxiosRequestConfig<D>): Promise<ApiResponse<T>> {
    const headers = {
      ...this.axios.defaults.headers.common,
      ...options?.headers,
      ...this.authHeader(),
    };

    const response = await this.axios.post(path, data, { ...options, headers });

    return response.data;
  }

  async put<T, D = unknown>(path: string, data: D, options: AxiosRequestConfig<D>): Promise<ApiResponse<T>> {
    const headers = {
      ...this.axios.defaults.headers.common,
      ...options.headers,
      ...this.authHeader(),
    };
    const response = await this.axios.put(path, data, { ...options, headers });

    return response.data;
  }

  async patch<T, D = unknown>(path: string, data: D, options: AxiosRequestConfig<D>): Promise<ApiResponse<T>> {
    const headers = {
      ...this.axios.defaults.headers.common,
      ...options.headers,
      ...this.authHeader(),
    };

    const response = await this.axios.patch(path, data, { ...options, headers });

    return response.data;
  }

  async delete<T>(path: string, options: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const headers = {
      ...this.axios.defaults.headers.common,
      ...options.headers,
      ...this.authHeader(),
    };

    const response = await this.axios.delete(path, { ...options, headers });

    return response.data;
  }

  getAxiosInstance(): AxiosInstance {
    return this.axios;
  }

  /** `Authorization` for the current token — or NOTHING when there is none, never `Bearer null`. */
  private authHeader(): { Authorization?: string } {
    return this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {};
  }

  setAuthToken(token: string | null) {
    this.authToken = token;
    // The constructor seeds the token as a TOP-LEVEL instance header, which axios merges over
    // `common`: left in place it would outlive a rotation or a clear (`request()` has no per-call
    // override, and the wrappers send no header without a token). Keep `common` the only default.
    delete (this.axios.defaults.headers as unknown as Record<string, unknown>).Authorization;
    if (token) {
      this.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete this.axios.defaults.headers.common["Authorization"];
    }
  }

  static isCancel(error: Error): boolean {
    return axios.isCancel(error);
  }
}
